// useArtifacts(id, { live?, enabled?, settle? }) → UseQueryResult<Artifact[]>     key ["artifacts", id]
// What the node stored for a job. Refreshed every 5 s while the run is live (dumps and frames arrive during the run).
// After the run ends the node still uploads what collection produced (behavior-report.json, the pcap, replay.mp4):
// `settle` keeps polling every 3 s until its `done(list)` says everything arrived or `until` (epoch ms) passes.
// A job never dispatched to a node has none: no request is made (the backend would answer 409); data is [] for it.
// pickArtifact(artifacts, kind) → the newest artifact of that kind.
import { useQuery } from "@tanstack/react-query";
import { api, isApiError } from "@/lib/api";
import type { Artifact } from "@/types";
import { keys } from "./keys";
import { jobIsLive, retryUnlessMissing, useJob } from "./useJob";

const ARTIFACTS_POLL_MS = 5000;
const SETTLE_POLL_MS = 3000;
const NONE: Artifact[] = [];

export type ArtifactSettle = { until: number; done: (artifacts: readonly Artifact[]) => boolean };

export function useArtifacts(id: string, opts: { live?: boolean; enabled?: boolean; settle?: ArtifactSettle | null } = {}) {
  const wanted = !!id && (opts.enabled ?? true);
  const { data: record } = useJob(id, { enabled: wanted });
  const dispatched = !!record?.node_id;
  const settle = opts.settle;
  return useQuery({
    queryKey: keys.artifacts(id),
    queryFn: async (): Promise<Artifact[]> => {
      try {
        return (await api.artifacts(id)).artifacts ?? [];
      } catch (err) {
        if (isApiError(err) && err.status === 409) return [];
        throw err;
      }
    },
    enabled: wanted && dispatched,
    placeholderData: record && !dispatched ? NONE : undefined,
    retry: retryUnlessMissing,
    refetchInterval: (q) => {
      if (opts.live ?? jobIsLive(id, record)) return ARTIFACTS_POLL_MS;
      if (settle && Date.now() < settle.until && !settle.done(q.state.data ?? NONE)) return SETTLE_POLL_MS;
      return false;
    },
  });
}

export function pickArtifact(artifacts: readonly Artifact[] | undefined, kind: string): Artifact | undefined {
  let best: Artifact | undefined;
  for (const a of artifacts ?? []) if (a.kind === kind && (!best || a.created_at > best.created_at)) best = a;
  return best;
}
